// src/components/module/almacen/ArticulosDataTable/ArticulosDataTable.tsx
import React, { useContext, useState, useCallback } from "react";
import { DataGrid, GridColDef, esES } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Paper,
  Typography,
  Stack,
  InputAdornment,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon, CloudUpload as ImportIcon,
  Search as SearchIcon, Close as ClearIcon,
} from "@mui/icons-material";
import { ArticuloItem } from "../../../../services/api/articulosApi";
import { DarkModeContext } from "../../../../context/darkModeContext";

const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0", accent: "#2383E2", accentBg: "#E8F0FE", hover: "rgba(55,53,47,0.04)", shadow: "0 1px 3px rgba(0,0,0,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444", accent: "#529CCA", accentBg: "#143A52", hover: "rgba(255,255,255,0.04)", shadow: "0 1px 3px rgba(0,0,0,0.2)" },
};

interface ArticulosDataTableProps {
  items: ArticuloItem[]; columns: GridColDef[];
  onEdit: (item: ArticuloItem) => void; onDelete: (id: number) => void;
  onStatusChange: (id: number, active: boolean) => void;
  onAdd: () => void; onImport: () => void; isLoading: boolean;
  page: number; pageSize: number; totalRows: number;
  onPageChange: (page: number) => void; onPageSizeChange: (pageSize: number) => void;
  onSearch: (term: string) => void; searchValue: string;
}

const ArticulosDataTable: React.FC<ArticulosDataTableProps> = ({
  items, columns, onAdd, onImport, isLoading,
  page, pageSize, totalRows, onPageChange, onPageSizeChange,
  onSearch, searchValue,
}) => {
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;
  const [localSearch, setLocalSearch] = useState(searchValue);
  const debounceRef = React.useRef<number | undefined>(undefined);

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearch(value);
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => onSearch(value), 400);
  }, [onSearch]);

  const handleClear = () => {
    setLocalSearch("");
    onSearch("");
  };

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${p.border}`, borderRadius: "12px", overflow: "hidden", bgcolor: p.bg, boxShadow: p.shadow }}>
        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, borderBottom: `1px solid ${p.border}` }}>
          <Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: p.text }}>Artículos</Typography>
            <Typography sx={{ fontSize: "0.82rem", color: p.textT, mt: 0.3 }}>
              {totalRows} {totalRows === 1 ? "registro" : "registros"}
              {searchValue && <Box component="span" sx={{ ml: 0.5, color: p.accent }}>— filtrando por "{searchValue}"</Box>}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            {/* Buscador server-side */}
            <TextField
              size="small"
              placeholder="Buscar por nombre, EAN o referencia..."
              value={localSearch}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{
                minWidth: 260,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px", bgcolor: p.bgS, fontSize: "0.84rem",
                  "& fieldset": { borderColor: p.border },
                  "&:hover fieldset": { borderColor: p.borderH },
                  "&.Mui-focused fieldset": { borderColor: p.accent },
                },
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: p.textT }} /></InputAdornment>,
                endAdornment: localSearch ? (
                  <InputAdornment position="end">
                    <Tooltip title="Limpiar búsqueda">
                      <IconButton size="small" onClick={handleClear} sx={{ p: 0.3 }}>
                        <ClearIcon sx={{ fontSize: 15, color: p.textT }} />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Button onClick={onImport} startIcon={<ImportIcon sx={{ fontSize: 16 }} />}
              sx={{ color: p.textS, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", border: `1px solid ${p.border}`, px: 2, whiteSpace: "nowrap", "&:hover": { bgcolor: p.hover, borderColor: p.borderH } }}>
              Importar Excel
            </Button>
            <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={onAdd}
              sx={{ bgcolor: p.accent, color: "#FFF", px: 2.5, py: 1, fontSize: "0.84rem", fontWeight: 600, borderRadius: "8px", whiteSpace: "nowrap", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" } }}>
              Nuevo Artículo
            </Button>
          </Stack>
        </Box>

        <Box sx={{ px: 1 }}>
          <DataGrid
            rows={items} columns={columns} page={page - 1} pageSize={pageSize}
            rowCount={totalRows} paginationMode="server"
            onPageChange={(n) => onPageChange(n + 1)} onPageSizeChange={onPageSizeChange}
            rowsPerPageOptions={[5, 10, 20, 50]} pagination
            autoHeight
            checkboxSelection={false} disableSelectionOnClick loading={isLoading}
            getRowId={(row) => row.Id}
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            sx={{
              border: "none", fontSize: "0.84rem",
              "& .MuiDataGrid-columnHeaders": { bgcolor: p.bgS, borderBottom: `1px solid ${p.border}`, borderTop: `1px solid ${p.border}`, minHeight: "44px !important", maxHeight: "44px !important" },
              "& .MuiDataGrid-columnHeaderTitle": { fontSize: "0.78rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" },
              "& .MuiDataGrid-columnSeparator": { display: "none" },
              "& .MuiDataGrid-row": { borderBottom: `1px solid ${p.border}`, "&:hover": { bgcolor: p.hover }, "&:last-child": { borderBottom: "none" } },
              "& .MuiDataGrid-cell": { borderBottom: "none", py: 1 },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
              "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${p.border}`, minHeight: 48 },
              "& .MuiTablePagination-root": { fontSize: "0.78rem", color: p.textS },
            }}
          />
        </Box>
      </Paper>
  );
};

export default ArticulosDataTable;
